/**
 * Notification panel — V3 R6.
 *
 * "The bell shows a count but opens nothing." This is the panel: latest
 * notifications grouped by day, unread highlighted, mark read / read-all /
 * snooze, and click-through to the entity.
 */

import { useNavigate } from 'react-router';
import { Bell, BellOff, Check, CheckCheck, Clock } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { useUiStore } from '@/stores/ui';
import {
  useMarkAllRead,
  useMarkRead,
  useNotifications,
  useSnooze,
  useUnreadCount,
  type NotificationView,
} from './api';

/** Groups by calendar day, newest first, with friendly headings. */
function groupByDay(rows: NotificationView[]): { label: string; rows: NotificationView[] }[] {
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86_400_000).toDateString();
  const buckets = new Map<string, NotificationView[]>();

  for (const row of rows) {
    const day = new Date(row.createdAt).toDateString();
    const label = day === today ? 'Today' : day === yesterday ? 'Yesterday' : day;
    buckets.set(label, [...(buckets.get(label) ?? []), row]);
  }
  return [...buckets.entries()].map(([label, group]) => ({ label, rows: group }));
}

function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function NotificationPanel() {
  const open = useUiStore((s) => s.notificationsOpen);
  const setOpen = useUiStore((s) => s.setNotificationsOpen);
  const { data: rows = [], isLoading } = useNotifications();
  const { data: unread = 0 } = useUnreadCount();
  const markRead = useMarkRead();
  const markAll = useMarkAllRead();
  const snooze = useSnooze();
  const navigate = useNavigate();

  const groups = groupByDay(rows);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`}
          data-testid="notification-bell"
          className="relative"
        >
          <Bell />
          {unread > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 grid min-w-[14px] place-items-center rounded-full px-[3px] text-[9px] font-semibold text-white"
              style={{ background: 'var(--danger)' }}
              aria-live="polite"
              data-testid="unread-badge"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-[380px] p-0"
        data-testid="notification-panel"
      >
        <header className="flex items-center gap-2 border-b border-[var(--border)] px-3 py-2">
          <span className="flex-1 text-[13px] font-medium">Notifications</span>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              data-testid="mark-all-read"
              onClick={() => markAll.mutate()}
            >
              <CheckCheck />
              Mark all read
            </Button>
          )}
        </header>

        <div className="max-h-[420px] overflow-y-auto">
          {isLoading ? (
            <div className="p-3">
              <Skeleton className="h-20" />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-4">
              <EmptyState
                icon={<BellOff className="size-5" />}
                title="Nothing waiting"
                description="Assignments, mentions and review requests land here."
              />
            </div>
          ) : (
            groups.map((group) => (
              <section key={group.label}>
                <h3 className="sticky top-0 bg-[var(--surface-2)] px-3 py-1 text-[11px] font-medium uppercase tracking-wide vt-muted">
                  {group.label}
                </h3>
                {group.rows.map((row) => (
                  <div
                    key={row._id}
                    data-testid="notification-row"
                    data-unread={row.isRead ? 'false' : 'true'}
                    className={`flex items-start gap-2 border-b border-[var(--border)] px-3 py-2 last:border-0 ${
                      row.isRead ? '' : 'bg-[var(--accent-soft)]/40'
                    }`}
                  >
                    {!row.isRead && (
                      <span
                        className="mt-1.5 size-1.5 shrink-0 rounded-full"
                        style={{ background: 'var(--accent)' }}
                        aria-label="Unread"
                      />
                    )}
                    <button
                      type="button"
                      className="min-w-0 flex-1 text-left"
                      onClick={() => {
                        if (!row.isRead) markRead.mutate(row._id);
                        setOpen(false);
                        if (row.entityKey) navigate(`/tasks/${row.entityKey}`);
                        else if (row.url) navigate(new URL(row.url, window.location.origin).pathname);
                      }}
                    >
                      <div className="text-[13px]">
                        {row.title}
                        {row.groupCount && row.groupCount > 1 ? (
                          <span className="vt-muted"> ({row.groupCount} updates)</span>
                        ) : null}
                      </div>
                      {row.body && <div className="truncate text-[12px] vt-muted">{row.body}</div>}
                      <div className="text-[11px] vt-muted">{timeOf(row.createdAt)}</div>
                    </button>

                    <div className="flex shrink-0 gap-0.5">
                      {!row.isRead && (
                        <button
                          type="button"
                          aria-label={`Mark "${row.title}" read`}
                          onClick={() => markRead.mutate(row._id)}
                          className="grid size-6 place-items-center rounded-[4px] vt-muted hover:bg-[var(--surface-2)]"
                        >
                          <Check className="size-3.5" />
                        </button>
                      )}
                      <button
                        type="button"
                        aria-label={`Snooze "${row.title}" for an hour`}
                        onClick={() => snooze.mutate({ id: row._id, hours: 1 })}
                        className="grid size-6 place-items-center rounded-[4px] vt-muted hover:bg-[var(--surface-2)]"
                      >
                        <Clock className="size-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </section>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
